import React from 'react';
import PropTypes from 'prop-types';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';
// doing this style of import to cut down on size (30kb)
import _cloneDeep from 'lodash/cloneDeep';

import styles from './PaymentInfo.module.scss';
import ZeroPaymentBox from './ZeroPaymentBox';
import PaymentInfoCTA from './PaymentInfoCTA';

const baseElementOptions = {
  style: {
    base: {
      color: '#424770',
      fontFamily: '"Satoshi", Helvetica, Arial, sans-serif',
      lineHeight: '1.5rem',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const cardNumberOptions = _cloneDeep(baseElementOptions);
cardNumberOptions.showIcon = true;

const PaymentInfo = ({
  displayCTA,
  validateCardNumber,
  validateCardExpiry,
  validateCardCVC,
  cardFields,
  amountOwed,
  contactEmail,
}) => {
  const displayZeroPaymentBox = () => {
    return (
      <ZeroPaymentBox copy="No payment information required" />
    );
  };

  // Displays the payment info
  const displayPaymentInfo = () => {
    const { cardNumber, cardExpiry, cardCVC } = cardFields;
    return (
      <form>
        {displayCTA && <PaymentInfoCTA contactEmail={contactEmail} />}
        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className={styles.label}>
            <div className={styles.label__title}>Card number</div>
            <span className={styles.required}>*</span>
            <div className={`${styles['stripe-element-input']} ${!cardNumber.valid ? styles['form-error__highlight'] : ''}`}>
              <CardNumberElement
                options={cardNumberOptions}
                onChange={validateCardNumber}
              />
            </div>
            {!cardNumber.valid && cardNumber.error && <p className={styles['form-error__message']}>{cardNumber.error}</p>}
          </label>
        </div>
        <div className={styles['expiry-cvc-container']}>
          <div className={styles['expiry-cvc-container__expiration_date']}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className={styles.label}>
              <div className={styles.label__title}>Expiration date</div>
              <span className={styles.required}>*</span>
              <div className={`${styles['stripe-element-input']} ${!cardExpiry.valid ? styles['form-error__highlight'] : ''}`}>
                <CardExpiryElement
                  options={baseElementOptions}
                  onChange={validateCardExpiry}
                />
              </div>
              {!cardExpiry.valid && cardExpiry.error && <p className={styles['form-error__message']}>{cardExpiry.error}</p>}
            </label>
          </div>
          <div className={styles['expiry-cvc-container__cvc']}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className={styles.label}>
              <div className={styles.label__title}>CVC</div>
              <span className={styles.required}>*</span>
              <div className={`${styles['stripe-element-input']} ${!cardCVC.valid ? styles['form-error__highlight'] : ''}`}>
                <CardCvcElement
                  options={baseElementOptions}
                  onChange={validateCardCVC}
                />
              </div>
              {!cardCVC.valid && cardCVC.error && <p className={styles['form-error__message']}>{cardCVC.error}</p>}
            </label>
          </div>
        </div>
      </form>
    );
  };

  return (
    <div>
      {/* When the amount owed is greater than zero we want to require the credit card payment
      info, but when the amount owed is zero we want to display the 'no payment required'
      banner. */}
      {amountOwed > 0 ? displayPaymentInfo() : displayZeroPaymentBox()}
    </div>
  );
};

PaymentInfo.propTypes = {
  displayCTA: PropTypes.bool.isRequired,
  validateCardNumber: PropTypes.func.isRequired,
  validateCardExpiry: PropTypes.func.isRequired,
  validateCardCVC: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  cardFields: PropTypes.object.isRequired,
  amountOwed: PropTypes.number.isRequired,
  contactEmail: PropTypes.string.isRequired,
};

export default PaymentInfo;
